import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { GlassCard } from "../components/common/ui";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | ok | error
  const [data, setData] = useState({});
  const [resent, setResent] = useState(false);
  const called = useRef(false);

  useEffect(() => {
    document.title = "Verify Email · Clacton Fireworks";
    if (called.current) return; // guard React StrictMode double-invoke (one-time token)
    called.current = true;
    if (!token) { setState("error"); setData({ message: "This verification link is invalid." }); return; }
    api.get(`/newsletter/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch((err) => { setData({ message: formatApiError(err.response?.data?.detail) || "This verification link is invalid or has expired." }); setState("error"); });
  }, [token]);

  return (
    <div className="mx-auto max-w-lg px-4 py-24" data-testid="verify-email-page">
      <GlassCard className="p-10 text-center" hover={false}>
        {state === "loading" && <Loader2 className="h-10 w-10 animate-spin text-gold mx-auto" data-testid="verify-loading" />}
        {state === "ok" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto" />
            <h1 className="mt-5 font-playfair text-3xl text-white">You're on the VIP List!</h1>
            <p className="mt-3 text-white/75">Your email is verified. We've emailed your welcome gift.</p>
            {data.coupon_code && (
              <div className="mt-6 rounded-2xl border border-dashed border-gold/50 bg-gold/10 px-6 py-5">
                <p className="text-sm text-white/70">Your exclusive 10% off code</p>
                <p className="mt-1 font-cinzel text-3xl tracking-[3px] cf-gold-metallic font-bold" data-testid="verify-coupon">{data.coupon_code}</p>
              </div>
            )}
            <Button asChild className="mt-7 cf-gold-btn font-semibold gap-2"><Link to="/fireworks"><Sparkles className="h-4 w-4" /> Shop Now</Link></Button>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error">
            <XCircle className="h-14 w-14 text-firered mx-auto" />
            <h1 className="mt-5 font-playfair text-2xl text-white">Verification link problem</h1>
            <p className="mt-3 text-white/75">{data.message}</p>
            {!resent && (
              <ResendForm defaultEmail={data.email} onDone={() => setResent(true)} />
            )}
            {resent && <p className="mt-6 text-emerald-400 text-sm" data-testid="verify-resent">A fresh verification link is on its way — please check your inbox.</p>}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function ResendForm({ defaultEmail, onDone }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try { await api.post("/newsletter/resend-verification", { email }); onDone(); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2" data-testid="resend-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
        className="flex-1 rounded-lg bg-black/30 border border-white/15 text-white px-3 py-2 text-sm" data-testid="resend-email" />
      <Button type="submit" disabled={loading} className="cf-gold-btn font-semibold gap-2" data-testid="resend-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send new link
      </Button>
    </form>
  );
}
